import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import {
  ClearOutlined,
  DragIndicatorOutlined,
  EditOutlined,
  CheckOutlined
} from '@mui/icons-material';

import {
  addRowSx,
  addInputCellSx,
  addActionCellSx,
  actionWrapperSx,
  inputSx,
  actionIconSx,
  clearIconSx,
  editIconButtonSx,
  actionSvgSx,
  editSvgSx,
  topicTextSx,
  addIconSx,
  iconButtonNoAnimationSx
} from '../Styles/InputStyles';

import {
  actionCellSx,
  actionWrapperRowSx,
  checkboxCellSx,
  checkboxSx,
  dragIconButtonSx,
  getTemplateRowSx,
  getTextCellSx,
  topicTextRowSx
} from './TemplateTextRowStyles';

const TemplateTextRow = ({
  item,
  showCheckbox,
  editing,
  editingValue,
  isDragging,
  isDragOver,
  onEditingValueChange,
  onToggle,
  onEdit,
  onSave,
  onCancel,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd
}) => {
  const handleRowClick = () => {
    if (!editing && showCheckbox) {
      onToggle();
    }
  };

  const handleCheckboxChange = event => {
    event.stopPropagation();

    if (showCheckbox) {
      onToggle();
    }
  };

  const handleEdit = event => {
    event.stopPropagation();
    onEdit();
  };

  const handleSave = event => {
    event.stopPropagation();
    onSave();
  };

  const handleCancel = event => {
    event.stopPropagation();
    onCancel();
  };

  const handleDragHandleClick = event => {
    event.stopPropagation();
  };

  return (
    <TableRow
      hover={!editing}
      selected={showCheckbox && Boolean(item.checked)}
      onClick={handleRowClick}
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        ...addRowSx,
        ...getTemplateRowSx({
          isDragging,
          isDragOver,
          showCheckbox,
          editing
        })
      }}
    >
      {showCheckbox && (
        <TableCell
          align="center"
          sx={{
            ...addInputCellSx,
            ...checkboxCellSx
          }}
        >
          <Checkbox
            size="small"
            checked={Boolean(item.checked)}
            disabled={editing}
            onChange={handleCheckboxChange}
            onClick={event => event.stopPropagation()}
            inputProps={{
              'aria-label': `Select ${item.text}`
            }}
            sx={checkboxSx}
          />
        </TableCell>
      )}

      <TableCell
        title={editing ? undefined : item.text}
        sx={{
          ...addInputCellSx,
          ...getTextCellSx({
            showCheckbox,
            editing
          })
        }}
      >
        {editing ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={editingValue}
            onChange={event =>
              onEditingValueChange(event.target.value)
            }
            onClick={event =>
              event.stopPropagation()
            }
            onKeyDown={event => {
              event.stopPropagation();

              if (event.key === 'Enter') {
                event.preventDefault();
                onSave();
              }

              if (event.key === 'Escape') {
                onCancel();
              }
            }}
            sx={inputSx}
          />
        ) : (
          <Typography
            component="span"
            sx={{
              ...topicTextSx,
              ...topicTextRowSx
            }}
          >
            {item.text}
          </Typography>
        )}
      </TableCell>

      <TableCell
        align="center"
        sx={{
          ...addActionCellSx,
          ...actionCellSx
        }}
      >
        <Box
          sx={{
            ...actionWrapperSx,
            ...actionWrapperRowSx
          }}
        >
          {editing ? (
            <>
              <IconButton
                size="small"
                aria-label={`Save ${item.text}`}
                disabled={!editingValue.trim()}
                onClick={handleSave}
                sx={{
                  ...actionIconSx,
                  ...addIconSx,
                  ...iconButtonNoAnimationSx
                }}
              >
                <CheckOutlined sx={actionSvgSx} />
              </IconButton>

              <IconButton
                size="small"
                aria-label="Cancel edit"
                onClick={handleCancel}
                sx={{
                  ...actionIconSx,
                  ...clearIconSx,
                  ...iconButtonNoAnimationSx
                }}
              >
                <ClearOutlined sx={actionSvgSx} />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                size="small"
                aria-label={`Edit ${item.text}`}
                onClick={handleEdit}
                sx={{
                  ...actionIconSx,
                  ...editIconButtonSx
                }}
              >
                <EditOutlined sx={editSvgSx} />
              </IconButton>

              <IconButton
                draggable
                size="small"
                aria-label={`Reorder ${item.text}`}
                onClick={handleDragHandleClick}
                onMouseDown={handleDragHandleClick}
                onDragStart={event => {
                  event.stopPropagation();
                  onDragStart(event);
                }}
                onDragEnd={event => {
                  event.stopPropagation();
                  onDragEnd(event);
                }}
                sx={{
                  ...actionIconSx,
                  ...dragIconButtonSx
                }}
              >
                <DragIndicatorOutlined sx={actionSvgSx} />
              </IconButton>
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

TemplateTextRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    checked: PropTypes.bool
  }).isRequired,

  showCheckbox: PropTypes.bool,

  editing: PropTypes.bool,

  editingValue: PropTypes.string,

  isDragging: PropTypes.bool,

  isDragOver: PropTypes.bool,

  onEditingValueChange: PropTypes.func,

  onToggle: PropTypes.func,

  onEdit: PropTypes.func,

  onSave: PropTypes.func,

  onCancel: PropTypes.func,

  onDragStart: PropTypes.func,

  onDragOver: PropTypes.func,

  onDrop: PropTypes.func,

  onDragEnd: PropTypes.func
};

TemplateTextRow.defaultProps = {
  showCheckbox: true,

  editing: false,

  editingValue: '',

  isDragging: false,

  isDragOver: false,

  onEditingValueChange: () => {},

  onToggle: () => {},

  onEdit: () => {},

  onSave: () => {},

  onCancel: () => {},

  onDragStart: () => {},

  onDragOver: () => {},

  onDrop: () => {},

  onDragEnd: () => {}
};

export default TemplateTextRow;